import React from 'react';

/**
 * Иконки шапки квиза в виде эталона Slimkit (spec shared/header.md,
 * «Геометрия»): шеврон 11×19 со скруглёнными концами (`back.svg`/`next.svg`)
 * и белая галочка узла прогресса (`point.svg`). Контуры перенесены из
 * исходников эталона как есть; цвет — бренд воронки (`--color-primary`),
 * а не зашитый синий, чтобы иконки следовали палитре.
 */

const BACK_PATH =
  'M8.21777 0.43932C8.80355 -0.146453 9.75308 -0.146426 10.3389 0.43932C10.9247 1.02511 10.9247 1.97463 10.3389 2.56041L3.61328 9.286L10.3389 16.0116C10.9247 16.5974 10.9247 17.5479 10.3389 18.1337C9.75318 18.7191 8.80352 18.719 8.21777 18.1337L0.439453 10.3553C0.146664 10.0625 0.000103653 9.67854 7.03549e-09 9.29479V9.27819C-3.7092e-05 8.89434 0.146647 8.51055 0.439453 8.21764L8.21777 0.43932Z';

const NEXT_PATH =
  'M2.56055 0.43932C1.97477 -0.146453 1.02524 -0.146426 0.439453 0.43932C-0.146334 1.02511 -0.146334 1.97463 0.439453 2.56041L7.16504 9.286L0.439453 16.0116C-0.146334 16.5974 -0.146334 17.5479 0.439453 18.1337C1.02514 18.7191 1.9748 18.719 2.56055 18.1337L10.3389 10.3553C10.6317 10.0625 10.7782 9.67854 10.7783 9.29479V9.27819C10.7784 8.89434 10.6317 8.51055 10.3389 8.21764L2.56055 0.43932Z';

const CHECK_PATH =
  'M11.7487 5.70276C12.035 5.43247 12.4991 5.43236 12.7854 5.70276C13.0715 5.97322 13.0716 6.41181 12.7854 6.68221L7.90514 11.298C7.76139 11.4338 7.57242 11.5006 7.38401 11.5C7.19553 11.5007 7.00674 11.4337 6.86287 11.298L4.21475 8.77611C3.92841 8.50565 3.92842 8.06713 4.21475 7.79666C4.50109 7.52632 4.9652 7.52624 5.25149 7.79666L7.38493 9.82832L11.7487 5.70276Z';

/** `color` — своё поле цвета значка шапки (buttonIconColor); нет — цвет бренда. */
export function ChevronIcon({
  direction,
  color,
}: {
  direction: 'back' | 'forward';
  color?: string;
}) {
  return (
    <svg
      width={11}
      height={19}
      viewBox="0 0 11 19"
      aria-hidden="true"
      focusable="false"
      data-testid={`quiz-chevron-${direction}`}
      style={{ display: 'block', opacity: 0.86 }}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d={direction === 'back' ? BACK_PATH : NEXT_PATH}
        fill={color ?? 'var(--color-primary, #2779ff)'}
      />
    </svg>
  );
}

export function NodeCheckIcon() {
  return (
    <svg
      width={17}
      height={17}
      viewBox="0 0 17 17"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <path d={CHECK_PATH} fill="#fff" />
    </svg>
  );
}

/**
 * Область нажатия шеврона: 44×24, иконка в коробке 24×24 прижата к центру
 * экрана (у «назад» — вправо, у «вперёд» — влево), поля 7 px. Без фона,
 * рамки и реакции на нажатие — как у эталона.
 */
export function chevronSlotStyle(
  direction: 'back' | 'forward',
): React.CSSProperties {
  return {
    minWidth: 44,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: direction === 'back' ? 'flex-end' : 'flex-start',
    paddingLeft: direction === 'forward' ? 7 : 0,
    paddingRight: direction === 'back' ? 7 : 0,
    background: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
    flexShrink: 0,
  };
}

/** Коробка иконки 24×24, иконка по центру. */
export const CHEVRON_BOX_STYLE: React.CSSProperties = {
  width: 24,
  height: 24,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * Круглая кнопка шапки без текста — форма `circle-solid` полей шапки квиза
 * (слово владельца 20.10.2026, дополнение; образец — Mojo): белый круг 36 px
 * с тонкой тенью, без рамки, внутри только иконка (стрелка «назад» или
 * крестик). Одна форма на обе кнопки, чтобы «назад» и крестик не разъехались.
 */
export const CIRCLE_SOLID_STYLE: React.CSSProperties = {
  width: 36,
  height: 36,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderRadius: '50%',
  background: '#fff',
  borderWidth: 0,
  borderStyle: 'none',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
  boxSizing: 'border-box',
  // Значок тёмный: круг всегда белый, а цвет текста тёмной темы светлый —
  // дал бы белое на белом. Свой цвет — полем шапки buttonIconColor.
  color: '#1a1a1a',
  cursor: 'pointer',
};

const ARROW_BACK_PATH = 'M14 8H2M7 3L2 8l5 5'; // i18n-allow: фигура SVG, не надпись

/** Стрелка «назад» 16×16 для круглой кнопки; цвет — от кнопки (currentColor). */
export function ArrowBackIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      aria-hidden="true"
      focusable="false"
      data-testid="quiz-arrow-back"
      style={{ display: 'block' }}
    >
      <path
        d={ARROW_BACK_PATH}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
